'use client'

import { useState } from 'react'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import emailjs from "@emailjs/browser";
import { addDays, format } from 'date-fns'

export default function BestellingBevestigen({ producten, naam, email, telefoon, bericht, onConfirm, onCancel }) {
  const [open, setOpen] = useState(true);
  const [dag, setDag] = useState('vrijdag');
  const [uur, setUur] = useState('09:00');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    const updatedFormData = {
      ...formData,
      dag: dag,
      uur: uur,
      producten: formData.producten.map(product => ({
        ...product,
        isPerPiece: product.isPerPiece,
      }))
    };

    try {
      const emailWinkel = emailjs.send(
        "service_mihiia7",
        "template_dceejh3",
        updatedFormData,
        "mE4uis-qyqmdfk5a1"
      );

      // E-mail naar de klant
      const emailKlant = emailjs.send(
        "service_mihiia7",
        "template_tzs0r16",
        {
          naam: naam,
          email: email,
          telefoon: telefoon,
          dag: dag,
          uur: uur,
          producten: gefilterdeProducten,
          bericht: bericht
        },
        "mE4uis-qyqmdfk5a1"
      );

      await Promise.all([emailWinkel, emailKlant]);

      setStatus("Bestelling succesvol verzonden");
      setFormData({
        producten: "",
        naam: "",
        email: "",
        telefoon: "",
        bericht: "",
        dag: "",
        uur: ""
      });
    } catch (error) {
      setStatus("Er ging iets mis bij het verzenden van de bestelling")
    } finally {
      setLoading(false);
    }

    onConfirm();
    setOpen(false);
  };

  const [status, setStatus] = useState("");

  const uren = [];
  for (let h = 9; h <= 18; h++) {
    uren.push(`${String(h).padStart(2, '0')}:00`);
    if (h !== 18) uren.push(`${String(h).padStart(2, '0')}:30`);
  }

  const gefilterdeProducten = producten
  .filter((product) => 
    parseFloat((product.gewicht || "0").toString().replace(",", ".")) > 0 || 
    (product.aantalStuks && product.aantalStuks > 0)
  )
  .map((product) => ({
    naam: product.naam,
    gewicht: product.gewicht || null, 
    aantalStuks: product.aantalStuks || null,
  }));

const [formData, setFormData] = useState({
  producten: gefilterdeProducten,
  naam: naam,
  email: email,
  telefoon: telefoon,
  bericht: bericht,
  dag: dag,
  uur: uur
});

const genereerBeschikbareDagen = () => {
  const dagen = [];
  const vandaag = new Date();

  for(let i = 0; i < 4; i++) {
    let vrijdag = addDays(vandaag, (5 - vandaag.getDay()) + i * 7);
    let zaterdag = addDays(vandaag, (6 - vandaag.getDay()) + i * 7);

    dagen.push({
      value: format(vrijdag, "yyyy-MM-dd"),
      label: `Vrijdag ${format(vrijdag, "dd/MM/yyyy")}`
    });

    dagen.push({
      value: format(zaterdag, "yyyy-MM-dd"),
      label: `Zaterdag ${format(zaterdag, "dd/MM/yyyy")}`
    });
  }
  return dagen;
}

const beschikbareDagen = genereerBeschikbareDagen();


  return (
    <Dialog open={open} onClose={() => setOpen(false)} className="relative z-10">
      <DialogBackdrop className="fixed inset-0 bg-gray-500/75" />

      <div className="fixed inset-0 z-10 flex items-center justify-center w-full h-full overflow-y-auto">
        <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:w-full sm:max-w-lg sm:px-6 sm:py-5">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex items-center justify-center rounded-full bg-green-100 sm:mx-0 sm:size-10">
                <ExclamationTriangleIcon className="h-6 w-6 sm:h-10 sm:w-10 text-green-600" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <DialogTitle as="h3" className="text-base font-semibold text-gray-900 sm:text-lg">
                  Bestelling Bevestigen
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 sm:text-base">
                    De bestelling wordt verzonden naar het e-mailadres <strong className="underline">{email}</strong>, op de naam <strong className="underline">{naam}</strong> met telefoonnummer <strong className="underline">{telefoon}</strong>.
                  </p>
                  {bericht && (
                    <p className="text-sm text-gray-500 sm:text-base mt-2">
                      En met volgende bericht: <strong className="underline">{bericht}</strong>
                    </p>
                  )}
                  {/* Maak de productenlijst scrollbaar als deze te lang is */}
                  <ul className="mt-3 list-disc pl-5 text-sm text-gray-600 sm:text-base max-h-60 overflow-y-auto">
                  {producten
                    .filter(product => product.aantalStuks > 0 || parseFloat(product.gewicht.toString().replace(",", ".")) > 0)
                    .map((product, index) => (
                      <li key={index}>
                        {product.naam} 
                        {product.aantalStuks > 0 && ` - ${product.aantalStuks} stuks`}
                        {product.isPerPiece}
                        {parseFloat(product.gewicht.toString().replace(",", ".")) > 0 && ` - ${product.gewicht} kg`}
                      </li>
                    ))}
                </ul>
                </div>

                {/* Keuze voor dag */}
                <label className="mt-10 block text-sm font-medium text-gray-700">Kies een dag:</label>
                <select
                    value={dag}
                    onChange={(e) => setDag(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md mb-4"
                >
                    {beschikbareDagen.map((dagOptie) => (
                        <option key={dagOptie.value} value={dagOptie.value}>{dagOptie.label}</option>
                    ))}
                </select>

                {/* Keuze voor uur */}
                <label className="block text-sm font-medium text-gray-700">Kies een uur:</label>
                <select
                    value={uur}
                    onChange={(e) => setUur(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md mb-4"
                >
                    {uren.map((tijd) => (
                        <option key={tijd} value={tijd}>{tijd}</option>
                    ))}
                </select>
              </div>
            </div>
          </div>

          {/* Toon de laadindicator of knoppen */}
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            {loading ? (
              <div className="flex justify-center items-center w-full">
                <svg
                  className="animate-spin h-5 w-5 mr-3 text-green-600"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 12a8 8 0 118 8"
                  />
                </svg>
                Bezig met verzenden...
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e)}
                  className="inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white sm:ml-3 sm:w-auto"
                >
                  Bevestig Bestelling
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false); 
                    onCancel();
                  }} 
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Annuleer
                </button>
              </>
            )}
          </div>

          {status && (
            <div className="mt-4 text-center text-sm text-green-600">
              {status}
            </div>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
}